import { isNil } from 'lodash';
import { CSSProperties, memo } from 'react';
import { IconName } from '@shared/components/Icon';
import { AddressField } from '@shared/data/entity/fields/AddressField';
import { AuditCheckboxField } from '@shared/data/entity/fields/AuditCheckboxField';
import { AuditTagField } from '@shared/data/entity/fields/AuditTagField';
import { AuditTextField } from '@shared/data/entity/fields/AuditTextField';
import { DateField } from '@shared/data/entity/fields/DateField';
import { DateRangeField } from '@shared/data/entity/fields/DateRangeField';
import { EmailField } from '@shared/data/entity/fields/EmailField';
import { isEntityBatchField } from '@shared/data/entity/fields/EntityBatchField';
import { Field, castField } from '@shared/data/entity/fields/Field';
import { FileField } from '@shared/data/entity/fields/FileField';
import { GeolocationField } from '@shared/data/entity/fields/GeolocationField';
import { NumberField } from '@shared/data/entity/fields/NumberField';
import { PersonField } from '@shared/data/entity/fields/PersonField';
import { RelatedEntityField } from '@shared/data/entity/fields/RelatedEntityField';
import { ScheduledAutomationField } from '@shared/data/entity/fields/ScheduledAutomationField';
import { SignatureField } from '@shared/data/entity/fields/SignatureField';
import { SubformField } from '@shared/data/entity/fields/SubformField';
import { TagField } from '@shared/data/entity/fields/TagField';
import { TextField } from '@shared/data/entity/fields/TextField';
import { TimeTrackerField } from '@shared/data/entity/fields/TimeTrackerField';
import { TodoField } from '@shared/data/entity/fields/TodoField';
import { UrlField } from '@shared/data/entity/fields/UrlField';
import { STATIC_COMPONENT_IDS } from '@shared/data/entity/staticComponentIds';
import { DateRangeEditField } from '@shared/scenes/entity/components/fields/DateRangeEditField';
import {
  isAddressComponent,
  isAuditCheckboxComponent,
  isAuditTagComponent,
  isAuditTextComponent,
  isCombinedTagsComponent,
  isDateComponent,
  isDateRangeComponent,
  isEmailComponent,
  isEntityBatchComponent,
  isFileComponent,
  isGeolocationComponent,
  isInputButtonComponent,
  isNumberComponent,
  isPersonComponent,
  isReferencedInComponent,
  isRelatedCardComponent,
  isRelatedCardLookupComponent,
  isScheduledAutomationComponent,
  isSignatureComponent,
  isStaticTextComponent,
  isSubformComponent,
  isSubtableComponent,
  isTagComponent,
  isTextComponent,
  isTimeTrackerComponent,
  isTodoComponent,
  isUrlComponent,
} from '@shared/scenes/workflows/utils';
import { API } from '@shared/types/api';
import { FieldRecord, UpdateField } from '../../types';
import { AddressEditField } from './AddressEditField';
import { AuditCheckboxEditField } from './AuditCheckboxEditField';
import { AuditTagEditField } from './AuditTagEditField';
import { AuditTextEditField } from './AuditTextEditField';
import { DateEditField } from './DateEditField';
import { EmailEditField } from './EmailEditField';
import { FileEditField } from './FileEditField';
import { GenericEditJsonField } from './GenericEditJsonField';
import { GeolocationEditField } from './GeolocationEditField';
import { InputButtonEditField } from './InputButtonEditField';
import { NumberEditField } from './NumberEditField';
import { PersonEditField } from './PersonEditField';
import { ReferencedInEditField } from './ReferencedInEditField';
import { RelatedCardLookupEditField } from './RelatedCardLookupEditField';
import { RelatedEntityEditField } from './RelatedEntityEditField';
import { ScheduledAutomationEditFieldNoCodeWrapper } from './ScheduledAutomationEditField';
import { SignatureEditField } from './SignatureEditField';
import { StaticTextEditField } from './StaticTextEditField';
import { SubformEditField } from './Subform/SubformEditField';
import { SubtableEditField } from './SubtableEditField';
import { TagEditField } from './TagEditField';
import { TextEditField } from './TextEditField';
import { TimeTrackerEditField } from './TimeTrackerEditField';
import { TodoEditField } from './TodoEditField';
import { UrlEditField } from './UrlEditField';

interface Props {
  field: Field | null;
  fields: FieldRecord;
  workflowTemplateId: string;
  component: API.AnyComponent;
  updateField: UpdateField;
  error?: string;
  entityId?: string;
  isAuthenticated?: boolean;
  allowClear?: boolean;
  icon?: IconName;
  style?: CSSProperties;
}

/**
 * Memoized so that typing in one field doesn't re-render every editor in the
 * form: each keystroke produces a new `fields` record, but an editor only
 * renders values from its own `field` plus the specific sibling ids below
 * (`getSiblingFieldIds`), so `fields` is compared per relevant entry rather
 * than by identity. Every other prop is compared by identity as usual.
 */
export const EditField = memo(EditFieldComponent, isEqualEditFieldProps);

function EditFieldComponent({
  field,
  fields,
  workflowTemplateId,
  component,
  updateField,
  error,
  entityId,
  isAuthenticated = true,
  allowClear = true,
  icon,
  style,
}: Props) {
  if (isEntityBatchComponent(component) || isCombinedTagsComponent(component)) {
    return null;
  }

  if (isAuditTagComponent(component)) {
    return (
      <AuditTagEditField
        component={component}
        field={castField(field, AuditTagField)}
        updateField={updateField}
        error={error}
        style={style}
      />
    );
  }
  if (isAuditTextComponent(component)) {
    return (
      <AuditTextEditField
        component={component}
        field={castField(field, AuditTextField)}
        updateField={updateField}
        error={error}
        style={style}
      />
    );
  }
  if (isAuditCheckboxComponent(component)) {
    return (
      <AuditCheckboxEditField
        field={castField(field, AuditCheckboxField)}
        component={component}
        updateField={updateField}
        error={error}
        style={style}
      />
    );
  }
  if (isStaticTextComponent(component)) {
    return <StaticTextEditField component={component} style={style} />;
  }

  if (isAddressComponent(component)) {
    return (
      <AddressEditField
        component={component}
        updateField={updateField}
        field={castField(field, AddressField)}
        error={error}
        style={style}
      />
    );
  }
  if (isDateComponent(component)) {
    return (
      <DateEditField
        component={component}
        updateField={updateField}
        field={castField(field, DateField)}
        workflowTemplateId={workflowTemplateId}
        entityBatchField={
          isEntityBatchField(fields.entityBatch) && component.allowEntityBatchCreation
            ? fields.entityBatch
            : undefined
        }
        error={error}
        allowClear={allowClear}
        style={style}
      />
    );
  }
  if (isDateRangeComponent(component)) {
    const startDate = fields[component.startDateComponentId];
    const endDate = fields[component.endDateComponentId];
    return (
      <DateRangeEditField
        field={castField(field, DateRangeField)}
        startDateField={castField(startDate, DateField)}
        endDateField={castField(endDate, DateField)}
        component={component}
        updateField={updateField}
        error={error}
        style={style}
      />
    );
  }

  if (isEmailComponent(component)) {
    return (
      <EmailEditField
        component={component}
        updateField={updateField}
        field={castField(field, EmailField)}
        error={error}
        allowClear={allowClear}
        style={style}
      />
    );
  }
  if (isFileComponent(component)) {
    return (
      <FileEditField
        component={component}
        updateField={updateField}
        field={castField(field, FileField)}
        error={error}
        style={style}
      />
    );
  }
  if (isGeolocationComponent(component)) {
    return (
      <GeolocationEditField
        component={component}
        updateField={updateField}
        field={castField(field, GeolocationField)}
        error={error}
        showUserName={isAuthenticated}
        style={style}
      />
    );
  }
  if (isNumberComponent(component)) {
    return (
      <NumberEditField
        component={component}
        updateField={updateField}
        field={castField(field, NumberField)}
        error={error}
        allowClear={allowClear}
        style={style}
      />
    );
  }
  if (isPersonComponent(component)) {
    if (!isAuthenticated) {
      return null;
    }
    return (
      <PersonEditField
        component={component}
        updateField={updateField}
        field={castField(field, PersonField)}
        workflowTemplateId={workflowTemplateId}
        error={error}
        allowClear={allowClear}
        style={style}
      />
    );
  }
  if (isRelatedCardComponent(component)) {
    return (
      <RelatedEntityEditField
        component={component}
        updateField={updateField}
        field={castField(field, RelatedEntityField)}
        fields={fields}
        error={error}
        allowCreate={isAuthenticated}
        allowClear={allowClear}
        entityId={entityId}
        style={style}
      />
    );
  }
  if (isSignatureComponent(component)) {
    return (
      <SignatureEditField
        component={component}
        updateField={updateField}
        field={castField(field, SignatureField)}
        error={error}
        style={style}
      />
    );
  }
  if (isSubformComponent(component)) {
    return (
      <SubformEditField
        component={component}
        updateField={updateField}
        field={castField(field, SubformField)}
        workflowTemplateId={workflowTemplateId}
        error={error}
        allowCreate={isAuthenticated}
        style={style}
      />
    );
  }
  if (isTagComponent(component)) {
    return (
      <TagEditField
        component={component}
        updateField={updateField}
        field={castField(field, TagField)}
        error={error}
        workflowTemplateId={workflowTemplateId}
        allowCreate={isAuthenticated && !!component.addAdditionalValues}
        allowClear={allowClear}
        style={style}
      />
    );
  }
  if (isTextComponent(component)) {
    return (
      <TextEditField
        component={component}
        updateField={updateField}
        field={castField(field, TextField)}
        error={error}
        allowClear={allowClear}
        icon={icon}
        style={style}
      />
    );
  }
  if (isTodoComponent(component)) {
    return (
      <TodoEditField
        component={component}
        updateField={updateField}
        field={castField(field, TodoField)}
        error={error}
        allowClear={allowClear}
        style={style}
      />
    );
  }
  if (isTimeTrackerComponent(component)) {
    return (
      <TimeTrackerEditField
        component={component}
        updateField={updateField}
        field={castField(field, TimeTrackerField)}
        error={error}
        style={style}
      />
    );
  }
  if (isInputButtonComponent(component)) {
    return (
      <InputButtonEditField
        component={component}
        inputField={fields[component.inputComponentId]}
        workflowTemplateId={workflowTemplateId}
        updateField={updateField}
        style={style}
      />
    );
  }
  if (isRelatedCardLookupComponent(component)) {
    return (
      <RelatedCardLookupEditField
        field={field}
        fields={fields}
        workflowTemplateId={workflowTemplateId}
        component={component}
        error={error}
        style={style}
      />
    );
  }
  if (isScheduledAutomationComponent(component)) {
    return (
      <ScheduledAutomationEditFieldNoCodeWrapper
        component={component}
        updateField={updateField}
        field={castField(field, ScheduledAutomationField)}
        dateComponentField={fields[component.dateComponentId] as DateField}
        error={error}
        isReadOnly={component.readonly ?? !isAuthenticated}
        workflowTemplateId={workflowTemplateId}
        showLabel
        allowClear={allowClear}
        style={style}
      />
    );
  }
  if (isReferencedInComponent(component)) {
    return !isNil(entityId) ? (
      <ReferencedInEditField component={component} entityId={entityId} style={style} />
    ) : null;
  }
  if (isSubtableComponent(component)) {
    return (
      <SubtableEditField
        component={component}
        entityId={entityId}
        workflowTemplateId={workflowTemplateId}
        isAuthenticated={isAuthenticated}
        style={style}
      />
    );
  }
  if (isUrlComponent(component)) {
    return (
      <UrlEditField
        component={component}
        updateField={updateField}
        field={castField(field, UrlField)}
        error={error}
        allowClear={allowClear}
        style={style}
      />
    );
  }

  return <GenericEditJsonField component={component} updateField={updateField} field={field} />;
}

function isEqualEditFieldProps(prev: Props, next: Props): boolean {
  if (
    prev.field !== next.field ||
    prev.component !== next.component ||
    prev.updateField !== next.updateField ||
    prev.error !== next.error ||
    prev.workflowTemplateId !== next.workflowTemplateId ||
    prev.entityId !== next.entityId ||
    prev.isAuthenticated !== next.isAuthenticated ||
    prev.allowClear !== next.allowClear ||
    prev.icon !== next.icon ||
    prev.style !== next.style
  ) {
    return false;
  }
  if (prev.fields === next.fields) {
    return true;
  }
  const siblingIds = getSiblingFieldIds(next.component);
  if (siblingIds === 'all') {
    return false;
  }
  return siblingIds.every(id => prev.fields[id] === next.fields[id]);
}

/**
 * The sibling entries of `fields` that the editor for this component renders,
 * mirroring the reads in EditFieldComponent above. 'all' for the editors that
 * traverse the whole record (related cards and lookups), which therefore
 * re-render on any field change.
 */
function getSiblingFieldIds(component: API.AnyComponent): string[] | 'all' {
  if (isDateComponent(component)) {
    return [STATIC_COMPONENT_IDS.ENTITY_BATCH];
  }
  if (isDateRangeComponent(component)) {
    return [component.startDateComponentId, component.endDateComponentId];
  }
  if (isInputButtonComponent(component)) {
    return [component.inputComponentId];
  }
  if (isScheduledAutomationComponent(component)) {
    return [component.dateComponentId];
  }
  if (isRelatedCardComponent(component) || isRelatedCardLookupComponent(component)) {
    return 'all';
  }
  return [];
}
